import { Component, OnDestroy, OnInit } from '@angular/core';
import { Client, ProductGroup } from '@app/core/models/client';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { ClientService } from '@app/core/services/client.service';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { TitleService } from '@app/core/services/title.service';
import { ClientHeaderComponent } from '../../../shared/client-header/client-header.component';
import { DatePipe, NgStyle } from '@angular/common';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../../shared/message-dialog/message-dialog.component';
import { Direction, Ordering } from '@app/core/utils/ordering';
import { ColumnSortIconComponent } from '../../../shared/column-sort-icon/column-sort-icon.component';
import { ProductGroupViewMode, ProductGroupViewPreferenceService } from '@app/core/services/product-group-view-preference.service';

type ProductGroupSortableField = 'name' | 'internal_name' | 'id' | 'vanity_url' | 'created_at' | 'updated_at';

const DATE_SORTABLE_FIELDS: ReadonlySet<ProductGroupSortableField> = new Set(['created_at', 'updated_at']);

@Component({
    templateUrl: './product-groups-list.component.html',
    styleUrls: ['./product-groups-list.component.scss'],
    standalone: true,
    imports: [ClientHeaderComponent, NgStyle, RouterLink, MessageDialogComponent_1, ColumnSortIconComponent, DatePipe]
})
export class ProductGroupsListComponent implements OnInit, OnDestroy {

  productGroups: ProductGroup[] = [];
  client: Client;
  headerGroups = {productGroups: true, inWireConsent: true, onboardingAndValidation: true};
  readonly limit = 100;
  offset = 0;
  viewMode: ProductGroupViewMode = 'tile';
  ordering: Ordering | undefined;
  dir = Direction;

  constructor(private activatedRoute: ActivatedRoute,
              private clientService: ClientService,
              private messageDialog: MessageDialogComponent,
              private titleService: TitleService,
              private viewPreferenceService: ProductGroupViewPreferenceService,
              private router: Router) {
  }

  ngOnInit() {
    this.viewMode = this.viewPreferenceService.getViewMode();
    this.getClient();
  }

  ngOnDestroy() {
    this.titleService.deactivate();
  }

  assignID(s: string) {
    return s.replace(/\s+/g, '');
  }

  navigateToPrevious(): void {
    this.router.navigateByUrl(`/client/${this.client.id}/edit/onboarding`);
  }

  navigateToReports(): void {
    this.router.navigateByUrl(`/client/${this.client.id}/report`);
  }

  toggleHeader(headerGroup: string) {
    if (this.headerGroups[headerGroup] !== undefined) {
      this.headerGroups[headerGroup] = !this.headerGroups[headerGroup];
    }
  }

  isDefault(product: ProductGroup): boolean {
    return product?.id === 'default';
  }

  setViewMode(mode: ProductGroupViewMode): void {
    this.viewMode = mode;
    this.viewPreferenceService.setViewMode(mode);
  }

  onColumnHeadingClick(column: ProductGroupSortableField): void {
    if (this.ordering?.orderBy === column) {
      this.ordering = this.ordering.direction === Direction.Asc
        ? new Ordering(column, Direction.Desc)
        : undefined;
    } else {
      this.ordering = new Ordering(column, Direction.Asc);
    }
  }

  get listViewProductGroups(): ProductGroup[] {
    if (!this.ordering) {
      return this.productGroups;
    }
    const orderBy = this.ordering.orderBy as ProductGroupSortableField;
    const sign = this.ordering.direction === Direction.Asc ? 1 : -1;
    return [...this.productGroups].sort((a, b) => sign * this.compareByField(a, b, orderBy));
  }

  private compareByField(a: ProductGroup, b: ProductGroup, field: ProductGroupSortableField): number {
    if (DATE_SORTABLE_FIELDS.has(field)) {
      return (new Date(a[field]).getTime() || 0) - (new Date(b[field]).getTime() || 0);
    }
    return String(a[field] ?? '').localeCompare(String(b[field] ?? ''));
  }

  private getProductGroups(): void {
    this.clientService.getProductGroupsByClient(this.client.id, this.limit, this.offset).subscribe(
      productGroups => {
        this.productGroups = this.sortDefaultFirst(this.productGroups.concat(productGroups));
      },
      error => {
        this.messageDialog.showMessage(`Oops...couldn't get a list of product groups for ${this.client.id}.`);
      }
    );
  }

  private sortDefaultFirst(productGroups: ProductGroup[]): ProductGroup[] {
    // There can be only one default product group per client; surface it at
    // the top of the list so it is easy to identify.
    return [...productGroups].sort((a, b) => {
      return Number(this.isDefault(b)) - Number(this.isDefault(a));
    });
  }

  private getClient(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.clientService.getClient(params['clientId']).subscribe(
        client => {
          this.client = client;
          this.getProductGroups();
        },
        error => {
          this.messageDialog.showMessage(`Oops...Could not load client: ${params['clientId']}`);
        },
        () => {
          this.setPrimaryTitle();
        });
    });
  }

  private setPrimaryTitle(): void {
    this.titleService.activate(
      this.client && this.client.company_name
        ? 'Configure Product Groups - ' + this.client.company_name
        : 'Configure Product Groups');
  }
}
